import { cva } from 'class-variance-authority';
import clsx from 'clsx';
import {
  ComponentPropsWithoutRef, FC, PropsWithChildren,
} from 'react';
import { useCountdownTimer } from '../../hooks/use-countdown-timer';
import { BaseOffer } from '../../types/offers';
import { Button, type ButtonVariant } from '../Button';
import { CountdownTimer } from '../CountdownTimer';
import { HourglassIcon, PowerIcon } from '../Icons';

const offerCardWrapper = cva(
  [
    'relative flex h-full flex-col rounded-lg text-white',
    'border-[1.5px] border-b-4 border-solid border-black',
    'transition-transform duration-300 ease-in-out hover:scale-105',
  ],
  {
    variants: {
      unavailable: {
        true: 'brightness-75 grayscale-[0.6]',
        false: '',
      },
    },
    defaultVariants: { unavailable: false },
  },
);

const bonusLabel = cva([
  'font-border poppins-md absolute z-10 flex items-center',
  'bg-[url(https://web-shop-assets.s3.eu-central-1.amazonaws.com/general/bonus-flag.png)] bg-cover bg-no-repeat',
  'left-[-14px] top-[-10px] h-[40.5px] w-[131.41px] pl-4',
  'md:-top-4 md:left-[-18px] md:h-[50px] md:w-[164.5px] md:pl-6',
]);

const expiresInLabel = cva([
  'font-border poppins-sm absolute inset-x-0 top-0',
  'flex items-center justify-end uppercase',
  'rounded-t-md bg-[#0E122C66] px-3 py-1',
  '[clip-path:polygon(0_0,100%_0%,100%_100%,0%_80%)]',
]);

const ghostImage = cva([
  'relative mt-6 flex-1 overflow-hidden',
  'flex min-h-[200px] items-center justify-center',
]);

const offerImage = cva(
  ['absolute left-1/2 top-[-15px] -translate-x-1/2'],
  {
    variants: {
      displayType: {
        grand: 'h-[230px] w-[303px] max-w-none',
        default: 'size-[230px]',
      },
      unavailable: {
        true: 'opacity-60',
        false: '',
      },
    },
    defaultVariants: { displayType: 'default', unavailable: false },
  },
);

const hardResoureceBadge = cva([
  'font-border poppins-lg w-max rounded-[10px] bg-[#0E122C33] px-[14px] py-[5px]',
]);

const offerInfo = cva(
  ['font-objektiv-mk3 flex h-[17.84px] items-center gap-1 text-[10px] font-black 2xl:text-[12.5px]'],
  {
    variants: {
      unavailable: {
        true: 'text-deactivatedWhite',
        false: 'text-[#CDB3F1]',
      },
    },
    defaultVariants: { unavailable: false },
  },
);

const offerName = cva([
  'font-border poppins-lg flex items-center justify-center text-center',
  'min-h-[50px] max-w-[200px]',
  'md:min-h-[70px] md:max-w-[250px]',
]);

type OfferCardWrapperProps = ComponentPropsWithoutRef<'div'> & PropsWithChildren & {
  unavailable: boolean,
  backgroundUrl: string
}

const OfferCardWrapper = ({
  children, unavailable: isUnavailable, backgroundUrl, className, ...props
}: OfferCardWrapperProps) => (
  <div
    className={offerCardWrapper({ unavailable: isUnavailable, className })}
    style={{
      backgroundImage: `url(${backgroundUrl})`,
      backgroundSize: 'cover',
    }}
    {...props}
  >
    {children}
  </div>
);

type OfferImageProps = ComponentPropsWithoutRef<'img'> & {
  src: string;
  displayType: BaseOffer['sku']['displayOfferType'];
  unavailable: boolean;
}

const OfferImage = ({
  src, displayType, unavailable, ...props
}: OfferImageProps) => (
  <img
    className={offerImage({ displayType: displayType === 'grand' ? 'grand' : 'default', unavailable })}
    src={src}
    alt="offer-item"
    {...props}
  />
);

const BottomBackground = () => (
  <div
    className="
      absolute
      inset-x-[-2px]
      bottom-[-4px]
      top-0
      z-0
      rounded-b-lg
      bg-black
      [clip-path:polygon(0_0,100%_20%,100%_100%,0%_100%)]
    "
  >
    <div
      className="
        absolute inset-x-[1.5px] bottom-[6px] top-[1.5px] rounded-b-lg
        [background:linear-gradient(179.98deg,#B149EC_21.24%,#541CAC_155.27%)]
        [box-shadow:0px_-2.44px_0px_0px_#330875_inset]
        [clip-path:polygon(0_0,100%_21%,100%_100%,0%_100%)]
      "
    />
  </div>
);

export type OfferCardProps = {
  name: string
  bonus?: string
  description?: string
  expiredAt: Date | undefined
  expiredInLabelText: (timeLeft: string) => string
  imageUrl: string
  backgroundUrl: string
  buttonText: string,
  buttonSubtext?: string,
  buttonType: ButtonVariant
  showPowerIcon?: boolean
  isUnavailable: boolean
  refreshTime?: Date
  displayOfferType: BaseOffer['sku']['displayOfferType']
  hardResource: number
  checkout: VoidFunction
}

export const OfferCard: FC<OfferCardProps> = ({
  name,
  bonus,
  expiredAt,
  expiredInLabelText,
  imageUrl,
  backgroundUrl,
  buttonText,
  buttonSubtext,
  buttonType,
  showPowerIcon = false,
  isUnavailable,
  description,
  refreshTime,
  displayOfferType,
  hardResource,
  checkout,
}) => {
  const timeLeft = useCountdownTimer(expiredAt ?? new Date());

  return (
    <OfferCardWrapper unavailable={isUnavailable} backgroundUrl={backgroundUrl}>
      {bonus && (<div className={bonusLabel()}>{bonus}</div>)}
      {expiredAt && (<div className={expiresInLabel()}>{expiredInLabelText(timeLeft)}</div>)}
      <div className={ghostImage()}>
        {hardResource && (
          <div className={hardResoureceBadge({ className: 'absolute inset-x-3 top-3' })}>
            {hardResource.toLocaleString()}
          </div>
        )}
      </div>
      <div className="relative z-0">
        <div className="absolute top-[-169px] flex h-[213px] w-full overflow-hidden">
          <OfferImage
            src={imageUrl}
            displayType={displayOfferType}
            unavailable={isUnavailable}
          />
        </div>
        <BottomBackground />
        <div className="relative z-10 flex flex-col items-center">
          <span className={offerName()}>
            {name}
          </span>
          <div className="flex flex-col items-center gap-2 px-[10px] pb-4 pt-2">
            <Button
              className={clsx('font-border flex w-[209px] flex-col gap-1 leading-none', isUnavailable && 'poppins-lg px-0')}
              variant={buttonType}
              size="2xl"
              disabled={isUnavailable}
              onClick={checkout}
            >
              <span className="flex items-center gap-1">
                {buttonText}
                {showPowerIcon && !isUnavailable && <PowerIcon className="size-[21px] md:size-[34px]" />}
              </span>
              {!isUnavailable && buttonSubtext && <p className="poppins-sm text-[#005737] [-webkit-text-stroke:0] [text-shadow:none]">{buttonSubtext}</p>}
            </Button>
            <div className={offerInfo({ unavailable: isUnavailable })}>
              {description}
              {refreshTime && (
                <div className="objektiv-mk3-sm flex items-center rounded-[2px] bg-black p-[2px] pr-1">
                  <HourglassIcon className="size-[15px]" />
                  <span className="h-[16px] lowercase">
                    <CountdownTimer targetDate={refreshTime} />
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </OfferCardWrapper>
  );
};
